<script setup lang="ts">
import { ref } from "vue";
import Dialog from "primevue/dialog";
import AutoComplete from "primevue/autocomplete";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { formatPrice } from "src/utils/recipe-formatters";
import type { FetchArticleResponseLite } from "src/api/models/fetchArticleResponseLite";

const visible = defineModel<boolean>("visible", { default: false });

const emit = defineEmits<{
  submit: [articleId: string];
}>();

const props = defineProps<{
  searchFn: (query: string) => Promise<FetchArticleResponseLite[]>;
  linkedArticleIds: string[];
  entityLabel: string;
}>();

const toast = useToast();
const selectedArticle = ref<FetchArticleResponseLite | null>(null);
const suggestions = ref<FetchArticleResponseLite[]>([]);
const searching = ref(false);

async function onSearch(event: { query: string }) {
  searching.value = true;
  try {
    const results = await props.searchFn(event.query);
    suggestions.value = results.filter(
      (a) => a.id && !props.linkedArticleIds.includes(a.id),
    );
  } catch {
    toast.add({ severity: "error", summary: "Error", detail: "Failed to search articles", life: 3000 });
    suggestions.value = [];
  } finally {
    searching.value = false;
  }
}

function onSubmit() {
  if (!selectedArticle.value?.id) return;
  emit("submit", selectedArticle.value.id);
  visible.value = false;
  selectedArticle.value = null;
}

function onHide() {
  selectedArticle.value = null;
  suggestions.value = [];
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :header="`Link Article to ${entityLabel}`"
    :modal="true"
    :style="{ width: '560px' }"
    @hide="onHide"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <label class="font-semibold text-sm">Search articles</label>
        <AutoComplete
          v-model="selectedArticle"
          :suggestions="suggestions"
          option-label="name"
          placeholder="Type to search articles..."
          :loading="searching"
          dropdown
          force-selection
          class="w-full"
          @complete="onSearch"
        >
          <template #option="{ option }">
            <div class="flex justify-between items-center w-full gap-4">
              <div>
                <span class="font-semibold">{{ option.name }}</span>
                <span v-if="option.code" class="text-color-secondary text-sm ml-2">({{ option.code }})</span>
              </div>
              <span class="text-sm text-color-secondary">{{ formatPrice(option.sale_price) }}</span>
            </div>
          </template>
        </AutoComplete>
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" severity="secondary" text @click="visible = false" />
      <Button
        label="Link"
        icon="pi pi-link"
        :disabled="!selectedArticle?.id"
        @click="onSubmit"
      />
    </template>
  </Dialog>
</template>
